import React, { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { toast } from "sonner";

/**
 * Vorsteuerpauschalierung-Block (nur Österreich) — Juni 2026.
 * Globale Stammdaten-Einstellung. Default AUS.
 */
export const VstpSettings = ({ user }) => {
  const [status, setStatus] = useState(null);
  const [regimes, setRegimes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [method, setMethod] = useState("base");
  const [branchKey, setBranchKey] = useState("");

  const load = useCallback(async () => {
    try {
      const [st, rg] = await Promise.all([
        api.get("/vstp/status"),
        api.get("/vstp/regimes"),
      ]);
      setStatus(st.data);
      setRegimes(rg.data);
      const cfg = st.data?.vorsteuer_pauschalierung;
      if (cfg?.enabled) {
        setMethod(cfg.method || "base");
        setBranchKey(cfg.branch_key || "");
      }
    } catch (e) {
      // still allow render with defaults
      setStatus({ available: user?.country === "AT", country: user?.country });
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Nur für Österreich anzeigen
  if (user?.country !== "AT") return null;

  const enabled = !!status?.vorsteuer_pauschalierung?.enabled;
  const available = status?.available !== false;

  // Speichert direkt. Parameter explizit übergeben, um Stale-State zu vermeiden.
  const save = async (nextEnabled, nextMethod = method, nextBranch = branchKey) => {
    setSaving(true);
    try {
      const payload = { enabled: nextEnabled };
      if (nextEnabled) {
        payload.method = nextMethod;
        if (nextMethod === "branch") payload.branch_key = nextBranch;
      }
      const res = await api.put("/vstp/settings", payload);
      setStatus((s) => ({ ...s, vorsteuer_pauschalierung: res.data.vorsteuer_pauschalierung }));
      toast.success(nextEnabled ? "Vorsteuerpauschalierung gespeichert" : "Vorsteuerpauschalierung deaktiviert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  // Toggle: aktiviert (mit Basismethode) bzw. deaktiviert — speichert sofort.
  const handleToggle = () => {
    if (saving) return;
    if (!enabled) {
      // Beim Aktivieren: bereits gewählte gültige Branche beibehalten, sonst Basis.
      if (method === "branch" && branchKey) save(true, "branch", branchKey);
      else { setMethod("base"); save(true, "base"); }
    } else {
      save(false);
    }
  };

  // Methodenwechsel — speichert direkt (Branche erst nach Auswahl).
  const handleMethodChange = (m) => {
    setMethod(m);
    if (m === "base") save(true, "base");
    else if (branchKey) save(true, "branch", branchKey);
  };

  const handleBranchChange = (k) => {
    setBranchKey(k);
    if (k) save(true, "branch", k);
  };

  return (
    <div className="border border-gray-200 rounded-xl p-4" data-testid="vstp-settings-block">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <span className="text-lg">🧮</span>
          <div>
            <h4 className="font-medium text-gray-900">Vorsteuerpauschalierung (AT)</h4>
            <p className="text-sm text-gray-600">
              Pauschaler Vorsteuerabzug statt Einzelbeleg-Erfassung (§ 14 UStG / Branchen-VO)
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span className={`text-sm font-semibold ${enabled ? "text-green-600" : "text-gray-500"}`} data-testid="vstp-status-label">
            {enabled ? "Aktiv" : "Inaktiv"}
          </span>
          <button
            type="button"
            disabled={saving || !available}
            data-testid="vstp-toggle"
            onClick={handleToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 ${
              enabled ? "bg-emerald-600" : "bg-gray-200"
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
      </div>

      {!available && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          Nicht verfügbar (nur Österreich, nicht für Kleinunternehmer).
        </p>
      )}

      {/* Auswahl erscheint erst nach Aktivierung. Änderungen speichern sofort. */}
      {available && enabled && (
        <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1">Art der Pauschalierung</label>
            <select
              data-testid="vstp-method-select"
              value={method}
              disabled={saving}
              onChange={(e) => handleMethodChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-60"
            >
              <option value="base">Basispauschalierung § 14 UStG (1,8 %, gedeckelt)</option>
              <option value="branch">Branchenpauschalierung (BGBl 627/1983)</option>
            </select>
          </div>

          {method === "branch" && (
            <div>
              <label className="block text-sm font-medium text-gray-800 mb-1">Branche</label>
              <select
                data-testid="vstp-branch-select"
                value={branchKey}
                disabled={saving}
                onChange={(e) => handleBranchChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-60"
              >
                <option value="">– Branche wählen –</option>
                {(regimes?.branches || []).map((b) => (
                  <option key={b.key} value={b.key}>
                    {b.label} — {b.rate.toLocaleString("de-DE", { minimumFractionDigits: 1 })} %
                  </option>
                ))}
              </select>
              {!branchKey && (
                <p className="text-xs text-amber-700 mt-1">Bitte eine Branche wählen — die Auswahl wird sofort gespeichert.</p>
              )}
            </div>
          )}

          <p className="text-xs text-emerald-800">
            Sonder-Vorsteuern (Anlagen, Wareneinkauf/Fremdleistung) bleiben zusätzlich abziehbar.
            Belege dafür im Beleg-Detail als „Wareneinkauf/Fremdleistung" markieren.
          </p>

          {status?.vorsteuer_pauschalierung?.activated_by === "sb" && (
            <p className="text-xs text-gray-600 italic">
              Durch Steuerberater eingerichtet{status?.vorsteuer_pauschalierung?.note ? `: ${status.vorsteuer_pauschalierung.note}` : ""}.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default VstpSettings;
